import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Pencil, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { parseCsv } from '@/utils/csv-helper';
import { SearchableSelect } from '@/components/ui/SearchableSelect';


/**
 * An invoice TOTAL is a whole rupee — that is what the backend stores, what the PDF prints and what
 * a remittance is matched against, so the screen must not be the one place it reads `.00`. The
 * subtotal and the CGST/SGST/IGST lines behind it keep their paise; the difference between the two
 * is the ROUND OFF line on the invoice itself.
 */
const amt0 = (n: number | string | null | undefined) => Math.round(Number(n ?? 0)).toLocaleString('en-IN');

type Invoice = {
  id: number;
  invoiceNo: string;
  invoiceTitle?: string | null;
  orderType: 'B2C' | 'B2B';
  isManual: boolean;
  merchantId: number;
  merchant?: { id: number; companyName: string; gstin?: string };
  /** Sales person owning this client (Merchant.salesUserId), resolved to a name by the API. */
  salesUserName?: string | null;
  gstin: string;
  companyName: string;
  invoiceDate: string;
  fromDate?: string | null;
  toDate?: string | null;
  dueDate?: string | null;
  subtotal: number;
  cgst: number;
  sgst: number;
  igst: number;
  totalAmount: number;
  status: string;
  paymentStatus?: string;
  paidAt?: string | null;
  paidAmount?: number | null;
  paidByName?: string | null;
  paymentTxnRef?: string | null;
  paymentMethod?: string | null;
  createdAt: string;
};

function InvoicesPage({ orderType }: { orderType: 'B2C' | 'B2B' }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isMerchant = user?.kind === 'merchant';
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [search, setSearch] = useState('');
  const [merchantFilter, setMerchantFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [payFor, setPayFor] = useState<Invoice | null>(null);
  const [editNoFor, setEditNoFor] = useState<Invoice | null>(null);

  const params = useMemo(() => {
    const p: any = { orderType };
    if (search) p.q = search;
    if (merchantFilter) p.merchantId = merchantFilter;
    if (statusFilter) p.status = statusFilter;
    if (typeFilter) p.type = typeFilter;
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    return p;
  }, [orderType, search, merchantFilter, statusFilter, typeFilter, fromDate, toDate]);

  const { data: invoices = [], isLoading } = useQuery<Invoice[]>({
    queryKey: ['invoices', params],
    queryFn: async () => (await api.get<Invoice[]>('/invoices', { params })).data,
  });

  const { data: merchants } = useQuery({
    queryKey: ['merchants-light-inv'],
    queryFn: async () => (await api.get<any>('/merchants', { params: { pageSize: 500 } })).data,
  });
  const merchantList = useMemo(
    () => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []),
    [merchants]
  );

  // Invoice / GST companies live under settings key `invoice_companies` as { items: [...] }
  // with snake_case fields (company_name, company_gstin). Normalise to { gstin, name }.
  const { data: gstCompanies = [] } = useQuery<any[]>({
    queryKey: ['invoice-companies-inv'],
    queryFn: async () => {
      const r: any = (await api.get('/settings/invoice_companies')).data;
      const arr = Array.isArray(r) ? r : (r?.items ?? []);
      return (arr as any[])
        .filter((c) => c && (c.company_gstin || c.gstin) && c.isActive !== false)
        .map((c) => ({ gstin: c.company_gstin || c.gstin, name: c.company_name || c.name || '' }));
    },
  });

  const statusMut = useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: string }) =>
      api.patch('/invoices/change-status', { ids, status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      setSelected({});
    },
  });

  const selectedIds = Object.keys(selected).filter((k) => selected[Number(k)]).map(Number);

  const handleExport = async () => {
    await downloadBlob(`/invoices/export?orderType=${orderType}`, `invoices-${orderType}.csv`);
  };

  const openPdf = (id: number) => {
    const w = window.open('', '_blank');
    api.get(`/invoices/${id}/pdf`, { responseType: 'text' })
      .then((r) => { if (w) { w.document.write(r.data as string); w.document.close(); } })
      .catch(() => w?.close());
  };

  // The same invoice as a spreadsheet. This used to be a second PDF button doing exactly what the
  // first one did.
  const downloadExcel = async (r: any) => {
    try {
      await downloadBlob(`/invoices/${r.id}/excel`, `${String(r.invoiceNo ?? r.id).replace(/[^A-Za-z0-9._-]/g, '_')}.xlsx`);
    } catch {
      toast.error('Could not download the Excel invoice.');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Invoices {orderType}</h1>
        <div className="flex gap-2">
          {!isMerchant && (
            <button
              disabled={selectedIds.length === 0}
              onClick={() => statusMut.mutate({ ids: selectedIds, status: 'SENT' })}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded text-sm disabled:opacity-50"
            >
              Change Status
            </button>
          )}
          <button onClick={handleExport} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm">
            Export Invoice[s]
          </button>
          {!isMerchant && (
            <button onClick={() => setImportOpen(true)} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm inline-flex items-center gap-1">
              <Upload size={14} /> Import Invoices
            </button>
          )}
          {!isMerchant && (
            <button onClick={() => { setEditing(null); setOpen(true); }} className="px-3 py-2 bg-emerald-600 text-white rounded text-sm">
              Generate Invoice
            </button>
          )}
        </div>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-12 gap-3 mb-4">
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Invoice No</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Enter Invoice No Here"
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Invoice Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
            <option value="">All</option>
            <option value="PENDING">Created</option>
            <option value="SENT">Sent</option>
            <option value="PAID">Paid</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Invoice Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
            <option value="">All</option>
            <option value="MANUAL">Manual</option>
            <option value="AUTOMATIC">Automatic</option>
          </select>
        </div>
        {!isMerchant && (
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Select Merchants</label>
          <SearchableSelect
            value={merchantFilter}
            onChange={(v) => setMerchantFilter(v ? Number(v) : '')}
            options={merchantList.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}${m.gstin ? ` · ${m.gstin}` : ''}`, hint: m.gstin ?? m.brandName ?? undefined }))}
            placeholder="All merchants"
          />
        </div>
        )}
        <div className="col-span-6">
          <label className="text-xs text-gray-500">Date Range</label>
          <div className="flex gap-1">
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="flex-1 border rounded px-2 py-1 text-sm" />
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="flex-1 border rounded px-2 py-1 text-sm" />
          </div>
        </div>
      </div>
      <div className="text-xs mb-2 text-gray-500">{selectedIds.length} Invoices Selected • Showing {invoices.length}</div>
      <div className="overflow-x-auto bg-white border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50">
            <tr>
              <th className="px-2 py-2">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    const c = e.target.checked;
                    const s: any = {};
                    if (c) invoices.forEach((i: Invoice) => { s[i.id] = true; });
                    setSelected(s);
                  }}
                />
              </th>
              <th className="px-2 py-2 text-left">Invoice Type</th>
              <th className="px-2 py-2 text-left">Invoice No.</th>
              {!isMerchant && <th className="px-2 py-2 text-left">Merchant Name</th>}
              {/* Who owns the account — collections chase an unpaid invoice through the sales person,
                  and without this the answer lived only in Merchant Master. Internal staff only. */}
              {!isMerchant && <th className="px-2 py-2 text-left">Sale Person</th>}
              <th className="px-2 py-2 text-left">GST No.</th>
              <th className="px-2 py-2 text-left">Invoice Date</th>
              <th className="px-2 py-2 text-left">Due Date</th>
              <th className="px-2 py-2 text-right">Invoice Amount</th>
              <th className="px-2 py-2 text-left">Invoice Status</th>
              <th className="px-2 py-2 text-left">Payment</th>
              <th className="px-2 py-2 text-left">Created At</th>
              <th className="px-2 py-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={isMerchant ? 11 : 13} className="text-center py-6 text-gray-400">Loading...</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={isMerchant ? 11 : 13} className="text-center py-6 text-gray-400">No invoices yet</td></tr>
            ) : (
              invoices.map((r: Invoice) => (
                <tr key={r.id} className="border-t">
                  <td className="px-2 py-2"><input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected({ ...selected, [r.id]: e.target.checked })} /></td>
                  <td className="px-2 py-2">{r.isManual ? 'Manual' : 'Automatic'}</td>
                  <td className="px-2 py-2 font-medium">{r.invoiceNo}</td>
                  {!isMerchant && <td className="px-2 py-2">{r.merchant?.companyName ?? '—'}</td>}
                  {!isMerchant && (
                    <td className={`px-2 py-2 text-xs ${r.salesUserName ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                      {r.salesUserName ?? 'Unassigned'}
                    </td>
                  )}
                  <td className="px-2 py-2">{r.gstin}</td>
                  <td className="px-2 py-2">{r.invoiceDate?.slice(0, 10)}</td>
                  <td className="px-2 py-2">{r.dueDate ? r.dueDate.slice(0, 10) : '—'}</td>
                  <td className="px-2 py-2 text-right">{amt0(r.totalAmount)}</td>
                  <td className="px-2 py-2">
                    <span className="text-blue-600 border border-blue-500 rounded-full px-2 py-0.5 text-xs">
                      ▸ {r.status === 'PENDING' ? 'Created' : r.status}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    {r.paymentStatus === 'PAID' ? (
                      <div className="text-xs">
                        <span className="inline-block bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Paid</span>
                        <div className="text-gray-500 mt-0.5" title={`Txn: ${r.paymentTxnRef ?? '—'}${r.paymentMethod ? ` · ${r.paymentMethod}` : ''}${r.paidAmount != null ? ` · ₹${amt0(r.paidAmount)}` : ''}`}>
                          {r.paidByName ? `by ${r.paidByName}` : ''}{r.paidAt ? ` · ${new Date(r.paidAt).toLocaleDateString('en-IN')}` : ''}
                        </div>
                      </div>
                    ) : isMerchant ? (
                      <span className="text-xs text-amber-700">Unpaid</span>
                    ) : (
                      <button onClick={() => setPayFor(r)} className="text-xs px-2 py-1 border border-emerald-600 text-emerald-700 rounded hover:bg-emerald-50">Mark Paid</button>
                    )}
                  </td>
                  <td className="px-2 py-2">{r.createdAt?.slice(0, 10)}</td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      <button title="PDF" onClick={() => openPdf(r.id)} className="border rounded px-2 py-1"><FileText size={14} className="inline" /></button>
                      <button title="Download Excel" onClick={() => downloadExcel(r)} className="border rounded px-2 py-1 text-emerald-700"><FileSpreadsheet size={14} className="inline" /></button>
                      <button title="Edit" onClick={() => { setEditing(r); setOpen(true); }} className="border rounded px-2 py-1"><Pencil size={14} className="inline" /></button>
                      {!isMerchant && !r.isManual && (
                        <button title="Edit Invoice No" onClick={() => setEditNoFor(r)} className="border rounded px-2 py-1 text-[11px] text-emerald-700">Edit No.</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {open && (
        <InvoiceForm
          orderType={orderType}
          initial={editing}
          merchants={merchantList}
          gstCompanies={gstCompanies}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={() => { setOpen(false); setEditing(null); qc.invalidateQueries({ queryKey: ['invoices'] }); }}
        />
      )}
      {importOpen && (
        <ImportInvoicesModal
          orderType={orderType}
          onClose={() => setImportOpen(false)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['invoices'] }); }}
        />
      )}

      {payFor && (
        <MarkPaidModal
          invoice={payFor}
          onClose={() => setPayFor(null)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['invoices'] }); setPayFor(null); }}
        />
      )}

      {editNoFor && (
        <EditInvoiceNoModal
          invoice={editNoFor}
          onClose={() => setEditNoFor(null)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['invoices'] }); setEditNoFor(null); }}
        />
      )}
    </div>
  );
}

function EditInvoiceNoModal({ invoice, onClose, onDone }: { invoice: Invoice; onClose: () => void; onDone: () => void }) {
  const [invoiceNo, setInvoiceNo] = useState(invoice.invoiceNo);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => (await api.patch(`/invoices/${invoice.id}/invoice-no`, { invoiceNo: invoiceNo.trim() })).data,
    onSuccess: () => onDone(),
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Failed to update invoice no'),
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-24 z-50" onClick={onClose}>
      <div className="bg-white rounded-lg w-[420px] max-w-[95vw]" onClick={(e) => e.stopPropagation()}>
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold">Edit Invoice No</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-gray-500">Correct the auto-generated invoice number. Must be unique.</div>
          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Invoice No *</label>
            <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
              className={`w-full border rounded px-2 py-1.5 text-sm ${!invoiceNo.trim() ? 'border-rose-300' : ''}`} />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!invoiceNo.trim() || invoiceNo.trim() === invoice.invoiceNo || save.isPending}
            className="px-4 py-2 bg-emerald-600 text-white rounded text-sm disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarkPaidModal({ invoice, onClose, onDone }: { invoice: Invoice; onClose: () => void; onDone: () => void }) {
  const [txnRef, setTxnRef] = useState('');
  const [method, setMethod] = useState('NEFT');
  // Pre-filled with the payable figure as it is billed — a whole rupee, so nobody has to decide
  // what to do about 89 paise the bank transfer will never carry.
  const [amount, setAmount] = useState(String(Math.round(Number(invoice.totalAmount))));
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/invoices/${invoice.id}/payment`, {
      paymentStatus: 'PAID',
      paymentTxnRef: txnRef.trim(),
      paymentMethod: method,
      paidAmount: amount,
      paidAt,
      paymentNote: note.trim() || undefined,
    })).data,
    onSuccess: () => onDone(),
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Failed to mark paid'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-20 z-50" onClick={onClose}>
      <div className="bg-white rounded-lg w-[460px] max-w-[95vw]" onClick={(e) => e.stopPropagation()}>
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold">Mark Invoice Paid — {invoice.invoiceNo}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-gray-500">Enter the transaction details. Your name and the current time are recorded automatically.</div>
          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Transaction Reference / UTR <span className="text-rose-600">*</span></label>
            <input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} placeholder="e.g. UTR / cheque no / txn id"
              className={`w-full border rounded px-2 py-1.5 text-sm ${!txnRef.trim() ? 'border-rose-300' : ''}`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                {['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'Cash', 'Adjustment', 'Other'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Amount Received</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="0.01"
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Payment Date</label>
            <input value={paidAt} onChange={(e) => setPaidAt(e.target.value)} type="date" className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!txnRef.trim() || save.isPending}
            className="px-4 py-2 bg-emerald-600 text-white rounded text-sm disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Mark Paid'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportInvoicesModal({ orderType, onClose, onDone }: { orderType: 'B2C' | 'B2B'; onClose: () => void; onDone: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<{ created: number; failed: number; errors: string[] } | null>(null);

  const onFile = (f: File | undefined) => {
    if (!f) return;
    setFileName(f.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result ?? ''));
      setRows(parsed.rows);
    };
    reader.readAsText(f);
  };

  const runImport = useMutation({
    mutationFn: async () => (await api.post(`/invoices/import?orderType=${orderType}`, { rows })).data,
    onSuccess: (r) => { setResult(r); onDone(); },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50">
      <div className="bg-white rounded-lg w-[640px] max-h-[85vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold">Import Invoices ({orderType})</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <p className="text-gray-600">
            Upload a CSV with these columns:
            <span className="block mt-1 font-mono text-xs bg-gray-50 border rounded p-2">
              GSTNO, Merchant ID, Invoice No, Invoice title, Subtotal, CGST %, SGST %, IGST %, Total, Invoice Date, Start Date, End Date, Due Date
            </span>
            Dates accept dd/mm/yyyy or yyyy-mm-dd. Leave <b>Invoice No</b> blank to auto-generate. If <b>Total</b> is blank it is computed from Subtotal + GST.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadBlob('/invoices/import-template', 'invoice-import-template.csv')}
              className="px-3 py-1.5 border border-emerald-600 text-emerald-700 rounded text-sm"
            >
              Download Sample CSV
            </button>
            <button onClick={() => fileRef.current?.click()} className="px-3 py-1.5 border rounded text-sm inline-flex items-center gap-1">
              <Upload size={14} /> Choose CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            {fileName && <span className="text-xs text-gray-500">{fileName} • {rows.length} row(s)</span>}
          </div>
          {result && (
            <div className={`rounded border p-3 text-xs ${result.failed ? 'border-amber-400 bg-amber-50' : 'border-emerald-400 bg-emerald-50'}`}>
              <div className="font-semibold">Imported {result.created} invoice(s){result.failed ? `, ${result.failed} failed` : ''}.</div>
              {result.errors?.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-amber-700 max-h-40 overflow-y-auto">
                  {result.errors.map((er, i) => <li key={i}>{er}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-1.5 rounded text-sm">Close</button>
          <button
            onClick={() => runImport.mutate()}
            disabled={rows.length === 0 || runImport.isPending}
            className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50"
          >
            {runImport.isPending ? 'Importing…' : `Import ${rows.length || ''} Invoice(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Add N days to a yyyy-mm-dd string, returning yyyy-mm-dd.
function addDaysStr(ymd: string, days: number): string {
  if (!ymd) return '';
  const d = new Date(ymd + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function InvoiceForm({ orderType, initial, merchants, gstCompanies, onClose, onSaved }: any) {
  const [mode, setMode] = useState<'AUTOMATIC' | 'MANUAL'>(
    initial ? (initial.isManual ? 'MANUAL' : 'AUTOMATIC') : 'AUTOMATIC'
  );
  const initialInvoiceDate = initial?.invoiceDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState<any>({
    gstin: initial?.gstin ?? gstCompanies?.[0]?.gstin ?? '',
    companyName: initial?.companyName ?? gstCompanies?.[0]?.name ?? '',
    merchantId: initial?.merchantId ?? '',
    invoiceNo: initial?.invoiceNo ?? '',
    invoiceTitle: initial?.invoiceTitle ?? '',
    subtotal: initial?.subtotal ?? 0,
    cgstPct: 9,
    sgstPct: 9,
    igstPct: 0,
    invoiceDate: initialInvoiceDate,
    fromDate: initial?.fromDate?.slice(0, 10) ?? '',
    toDate: initial?.toDate?.slice(0, 10) ?? '',
    // Default the payment due date to invoice date + 30 days (autofill) unless one is already saved.
    dueDate: initial?.dueDate?.slice(0, 10) ?? addDaysStr(initialInvoiceDate, 30),
    attachmentUrl: initial?.attachmentUrl ?? '',
  });
  const [attachmentName, setAttachmentName] = useState('');
  // Remember whether the user hand-edited the due date so auto-fill (+30) doesn't clobber their value.
  const dueTouched = useRef(!!initial?.dueDate);

  const selectedMerchantForGst = merchants.find((m: any) => m.id === Number(form.merchantId));
  const merchantGstin: string = selectedMerchantForGst?.gstin ?? '';

  // Auto-set CGST/SGST vs IGST from the GST state codes: same first 2 digits (same state) → CGST 9% + SGST 9%,
  // otherwise inter-state → IGST 18%. Runs whenever the seller GST or merchant changes.
  useEffect(() => {
    const sellerCode = (form.gstin ?? '').trim().slice(0, 2);
    const buyerCode = (merchantGstin ?? '').trim().slice(0, 2);
    if (!sellerCode || !buyerCode) return; // need both GSTINs to decide
    const sameState = sellerCode === buyerCode;
    setForm((s: any) => {
      const next = sameState
        ? { cgstPct: 9, sgstPct: 9, igstPct: 0 }
        : { cgstPct: 0, sgstPct: 0, igstPct: 18 };
      if (Number(s.cgstPct) === next.cgstPct && Number(s.sgstPct) === next.sgstPct && Number(s.igstPct) === next.igstPct) return s;
      return { ...s, ...next };
    });
  }, [form.gstin, merchantGstin]);

  // Keep the due date at invoice date + 30 days until the user overrides it.
  useEffect(() => {
    if (dueTouched.current) return;
    setForm((s: any) => ({ ...s, dueDate: addDaysStr(s.invoiceDate, 30) }));
  }, [form.invoiceDate]);

  // Auto-generate the next invoice number for new AUTOMATIC invoices (shown read-only).
  const { data: nextNo } = useQuery<{ invoiceNo: string }>({
    queryKey: ['invoice-next-no', orderType],
    queryFn: async () => (await api.get(`/invoices/next-no?orderType=${orderType}`)).data,
    enabled: !initial,
  });
  const isAuto = mode === 'AUTOMATIC';
  const autoNo = !initial && isAuto ? (nextNo?.invoiceNo ?? '') : '';
  // The invoice-number field is user-entered whenever it's not a brand-new AUTOMATIC invoice.
  const numberIsManualEntry = !(!initial && isAuto);
  const numberMissing = numberIsManualEntry && !(form.invoiceNo ?? '').toString().trim();

  const onAttach = (f: File | undefined) => {
    if (!f) return;
    setAttachmentName(f.name);
    const reader = new FileReader();
    reader.onload = () => setForm((s: any) => ({ ...s, attachmentUrl: String(reader.result ?? '') }));
    reader.readAsDataURL(f);
  };
  const total = useMemo(() => {
    const s = Number(form.subtotal ?? 0);
    const c = (Number(form.cgstPct ?? 0) / 100) * s;
    const sg = (Number(form.sgstPct ?? 0) / 100) * s;
    const i = (Number(form.igstPct ?? 0) / 100) * s;
    // Whole rupees, exactly as the server will store it — a preview that disagrees with the saved
    // invoice by 11 paise is worse than no preview.
    return Math.round(s + c + sg + i);
  }, [form.subtotal, form.cgstPct, form.sgstPct, form.igstPct]);

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, mode, invoiceNo: (!initial && isAuto ? autoNo : form.invoiceNo) };
      if (initial) return (await api.patch(`/invoices/${initial.id}`, body)).data;
      return (await api.post(`/invoices/${orderType.toLowerCase()}`, body)).data;
    },
    onSuccess: onSaved,
  });

  const selectedMerchant = merchants.find((m: any) => m.id === Number(form.merchantId));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[820px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold">{initial ? 'Edit Invoice' : 'Add Invoice'}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="border-b px-5">
          <div className="flex gap-6">
            <button onClick={() => setMode('AUTOMATIC')} className={`py-2 ${mode === 'AUTOMATIC' ? 'text-blue-700 border-b-2 border-blue-700 font-semibold' : 'text-gray-500'}`}>
              AUTOMATIC
            </button>
            <button onClick={() => setMode('MANUAL')} className={`py-2 ${mode === 'MANUAL' ? 'text-blue-700 border-b-2 border-blue-700 font-semibold' : 'text-gray-500'}`}>
              MANUAL
            </button>
          </div>
        </div>
        <div className="p-5 grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-500">Select GST No.</label>
            <select
              value={form.gstin}
              onChange={(e) => {
                const c = gstCompanies.find((g: any) => g.gstin === e.target.value);
                setForm({ ...form, gstin: e.target.value, companyName: c?.name ?? '' });
              }}
              className="w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="">Select…</option>
              {gstCompanies.map((g: any) => (
                <option key={g.gstin} value={g.gstin}>{g.name} ({g.gstin})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500">Select Merchant</label>
            <SearchableSelect
              value={form.merchantId}
              onChange={(v) => setForm({ ...form, merchantId: v ? Number(v) : '' })}
              options={merchants.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}${m.gstin ? ` · ${m.gstin}` : ''}`, hint: m.gstin ?? m.brandName ?? undefined }))}
              placeholder="Search merchant…"
            />
            {selectedMerchant?.gstin && <div className="text-xs text-gray-500 mt-1">Merchant GST: {selectedMerchant.gstin}</div>}
          </div>
          <div>
            <label className="block text-xs text-gray-500">
              Invoice No{' '}
              {!initial && isAuto
                ? <span className="text-emerald-600">(auto-generated)</span>
                : <span className="text-amber-600">(enter manually)</span>}
            </label>
            {!initial && isAuto ? (
              <input value={autoNo} readOnly className="w-full border rounded px-2 py-1.5 text-sm bg-gray-100 text-gray-600" placeholder="Generating…" />
            ) : (
              <input
                value={form.invoiceNo}
                onChange={(e) => setForm({ ...form, invoiceNo: e.target.value })}
                placeholder="Type invoice number"
                className={`w-full border rounded px-2 py-1.5 text-sm ${numberMissing ? 'border-red-400' : ''}`}
              />
            )}
            {numberMissing && <div className="text-xs text-red-500 mt-1">Enter an invoice number for a manual invoice.</div>}
          </div>
          {mode === 'MANUAL' && (
            <>
              <div>
                <label className="block text-xs text-gray-500">Invoice Title</label>
                <input value={form.invoiceTitle} onChange={(e) => setForm({ ...form, invoiceTitle: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Subtotal</label>
                <input type="number" value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
            </>
          )}
          <div>
            <label className="block text-xs text-gray-500">CGST %</label>
            <input type="number" value={form.cgstPct} onChange={(e) => setForm({ ...form, cgstPct: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500">SGST %</label>
            <input type="number" value={form.sgstPct} onChange={(e) => setForm({ ...form, sgstPct: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500">IGST %</label>
            <input type="number" value={form.igstPct} onChange={(e) => setForm({ ...form, igstPct: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
            {form.gstin && merchantGstin && (
              <div className="text-[11px] text-gray-400 mt-1">
                {form.gstin.slice(0, 2) === merchantGstin.slice(0, 2)
                  ? `Same state (${merchantGstin.slice(0, 2)}) → CGST 9% + SGST 9%`
                  : `Inter-state (${form.gstin.slice(0, 2)}→${merchantGstin.slice(0, 2)}) → IGST 18%`}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500">Invoice Date</label>
            <input type="date" value={form.invoiceDate} onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500">Start Date</label>
            <input type="date" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500">End Date</label>
            <input type="date" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500">Due Date <span className="text-gray-400">(auto +30 days)</span></label>
            <input type="date" value={form.dueDate} onChange={(e) => { dueTouched.current = true; setForm({ ...form, dueDate: e.target.value }); }} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          {mode === 'MANUAL' && (
            <div className="col-span-3">
              <label className="block text-xs text-gray-500">Attachment (optional)</label>
              <div className="flex items-center gap-2">
                <input type="file" accept="image/*,application/pdf" onChange={(e) => onAttach(e.target.files?.[0])} className="text-sm" />
                {form.attachmentUrl && !attachmentName && <span className="text-xs text-emerald-600">File on record</span>}
                {attachmentName && <span className="text-xs text-gray-500">{attachmentName}</span>}
                {form.attachmentUrl && (
                  <button type="button" onClick={() => { setForm({ ...form, attachmentUrl: '' }); setAttachmentName(''); }} className="text-xs text-red-500">Remove</button>
                )}
              </div>
            </div>
          )}
          {mode === 'MANUAL' && <div className="col-span-3 text-sm font-semibold">Total : {amt0(total)}</div>}
          {mode === 'AUTOMATIC' && (
            <div className="col-span-3 text-xs text-gray-500">
              In Automatic mode the freight totals for the selected period are aggregated and used as the subtotal.
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end items-center gap-3">
          {save.isError && (
            <span className="text-red-600 text-xs mr-auto">
              {(save.error as any)?.response?.data?.message ?? 'Could not save invoice. Please check the details.'}
            </span>
          )}
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || numberMissing || !form.merchantId} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function InvoicesB2cPage() {
  return <InvoicesPage orderType="B2C" />;
}
export function InvoicesB2bPage() {
  return <InvoicesPage orderType="B2B" />;
}
